import React, { useState } from 'react'; // Ajout de useState
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL, formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input'; // Ajout
import { Label } from '../components/ui/label'; // Ajout
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Zap, CheckCircle2, FileText, Loader2, PenTool } from 'lucide-react'; // Ajout PenTool
import PoweredBy from '../components/ui/PoweredBy';

const PortalQuotePage = () => {
  const { token } = useParams();
  const queryClient = useQueryClient();

  // Nouveaux états pour la signature sécurisée
  const [signerName, setSignerName] = useState('');
  const [cgvAccepted, setCgvAccepted] = useState(false);

  const { data: quote, isLoading, error } = useQuery({
    queryKey: ['portal-quote', token],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/portal/quotes/${token}`);
      if (!res.ok) throw new Error('Devis introuvable');
      return res.json();
    },
  });

  const signMutation = useMutation({
    mutationFn: async () => {
      // 🔴 ON ENVOIE MAINTENANT LE NOM DU SIGNATAIRE AU BACKEND
      const res = await fetch(`${API_URL}/portal/quotes/${token}/sign`, { 
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ signer_name: signerName }) 
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      return res.json();
    },
    onSuccess: () => {
        toast.success('Devis signé avec succès !');
        queryClient.invalidateQueries(['portal-quote', token]); // Rafraîchit la page pour afficher "Signé"
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl"><CardContent className="p-8 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-40 w-full" />
        </CardContent></Card>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md"><CardContent className="p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-heading font-bold mb-2">Devis introuvable</h2>
          <p className="text-muted-foreground">Ce lien est invalide ou a expiré.</p>
        </CardContent></Card>
      </div>
    );
  }

  const company = quote.company || {}; 
  const companyName = company.name || 'Votre Partenaire';
  const companyLogo = company.logo_base64;

  const isSigned = quote.status === 'signed' || quote.status === 'invoiced';
  const canSign = ['draft', 'sent', 'accepted'].includes(quote.status);
  
  // Validation : le bouton n'est actif que si le nom est rempli et la case cochée
  const isSignReady = signerName.trim().length > 2 && cgvAccepted;

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 pb-20">
      
      {/* Header Sticky */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-4">
            <div className="flex items-center gap-3">
            {companyLogo ? (
                <img src={companyLogo} alt="Logo" className="h-10 w-auto object-contain" />
            ) : (
                <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center text-white shadow-md">
                    <Zap className="w-5 h-5" />
                </div>
            )}
            <div>
                <p className="font-bold text-lg leading-tight text-slate-900">{companyName}</p>
                <p className="text-xs text-slate-500 font-medium tracking-wide uppercase">Devis Officiel</p>
            </div>
            </div>
            
            <Badge className={`px-3 py-1 font-bold ${isSigned ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-blue-100 text-blue-800 border-blue-200'}`} variant="outline">
                {isSigned ? 'Signé' : quote.status === 'sent' ? 'En attente' : quote.status}
            </Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        
        {/* En-tête Devis */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-1">Devis {quote.reference}</h1>
            <p className="text-slate-600 font-medium text-lg">
              {quote.client?.name && `${quote.client.name}`}
              {quote.client?.company && ` - ${quote.client.company}`}
            </p>
          </div>
          
          {(quote.start_date || quote.validity_date || quote.created_at) && (
             <div className="text-sm text-slate-500 space-y-1 bg-slate-50 p-3 rounded-lg border border-slate-100 min-w-[200px]">
               {quote.created_at && <div>Émis le : <strong className="text-slate-700">{formatDate(quote.created_at)}</strong></div>}
               {quote.start_date && <div>Période : <strong className="text-slate-700">{formatDate(quote.start_date)}</strong></div>}
               {quote.validity_date && <div>Valide jusqu'au : <strong className="text-slate-700">{formatDate(quote.validity_date)}</strong></div>}
             </div>
          )}
        </div>

        {quote.intro_text && (
            <div className="px-2 text-slate-600 leading-relaxed whitespace-pre-wrap">
                {quote.intro_text}
            </div>
        )}

        {/* Tableau des prestations */}
        <Card className="overflow-hidden border-slate-200 shadow-sm rounded-2xl bg-white">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                  <tr>
                    <th className="p-4">Désignation</th>
                    <th className="p-4 text-center w-20">Qté</th>
                    <th className="p-4 text-right w-32">P.U. HT</th>
                    <th className="p-4 text-right w-32">Total HT</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {quote.items?.map((item, i) => (
                    <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                      <td className="p-4">
                        <p className="font-bold text-slate-900">{item.product_name}</p>
                        {item.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.description}</p>}
                      </td>
                      <td className="p-4 text-center font-medium bg-slate-50/30">{item.quantity}</td>
                      <td className="p-4 text-right text-slate-600">{formatCurrency(item.unit_price)}</td>
                      <td className="p-4 text-right font-bold text-slate-900">{formatCurrency(item.quantity * item.unit_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Totaux */}
        <div className="flex justify-end">
            <Card className="w-full md:w-80 border-slate-200 shadow-sm rounded-2xl bg-white">
            <CardContent className="p-5 space-y-3">
                <div className="flex justify-between text-slate-600">
                <span>Total HT</span>
                <span className="font-semibold text-slate-900">{formatCurrency(quote.total_ht)}</span>
                </div>
                {quote.total_ttc !== quote.total_ht && (
                <div className="flex justify-between text-slate-600">
                    <span>TVA</span>
                    <span>{formatCurrency(quote.total_ttc - quote.total_ht)}</span>
                </div>
                )}
                <Separator className="my-2 bg-slate-200" />
                <div className="flex justify-between text-xl items-center">
                <span className="font-extrabold text-slate-900">Total TTC</span>
                <span className="font-extrabold text-primary bg-primary/10 px-3 py-1 rounded-lg">{formatCurrency(quote.total_ttc || quote.total_ht)}</span>
                </div>
            </CardContent>
            </Card>
        </div>

        {quote.footer_note && <p className="text-sm text-slate-500 whitespace-pre-wrap px-2">{quote.footer_note}</p>}
        {quote.payment_terms && <p className="text-xs text-slate-400 px-2 font-medium uppercase tracking-wide">Conditions de paiement : {quote.payment_terms}</p>}

        {/* --- ZONE DE SIGNATURE SÉCURISÉE --- */}
        {canSign && (
          <Card className="border-2 border-primary/20 bg-white shadow-xl overflow-hidden rounded-2xl mt-8">
            <div className="bg-primary/5 p-6 border-b border-primary/10 flex items-center gap-3">
                <div className="bg-primary text-white p-2 rounded-lg">
                    <PenTool className="w-5 h-5" />
                </div>
                <div>
                    <h3 className="font-extrabold text-xl text-slate-900">Validation et Signature</h3>
                    <p className="text-sm text-slate-600">Montant total engageant : <strong>{formatCurrency(quote.total_ttc || quote.total_ht)}</strong></p>
                </div>
            </div>
            
            <CardContent className="p-6 space-y-6">
              
              <div className="space-y-3">
                  <Label className="text-slate-700 font-bold text-base">Prénom et Nom du signataire *</Label>
                  <Input 
                      placeholder="Ex: Jean Dupont, Gérant" 
                      className="h-12 text-lg bg-slate-50 border-slate-300 focus:bg-white"
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      disabled={signMutation.isPending}
                  />
                  <p className="text-xs text-slate-500">Cette information fait office de signature électronique simple.</p>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => !signMutation.isPending && setCgvAccepted(!cgvAccepted)}>
                  <input 
                      type="checkbox" 
                      className="mt-1 h-5 w-5 rounded border-slate-300 text-primary cursor-pointer"
                      checked={cgvAccepted}
                      onChange={(e) => setCgvAccepted(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={signMutation.isPending}
                  />
                  <label className="text-sm text-slate-700 leading-snug cursor-pointer select-none">
                      Je déclare avoir lu et accepter les Conditions Générales de Vente. <strong>Je reconnais que la validation de ce devis m'engage fermement au paiement de la commande.</strong>
                  </label>
              </div>

              <Button
                size="lg" 
                className="w-full h-14 text-lg font-bold shadow-lg transition-all"
                onClick={() => signMutation.mutate()}
                disabled={!isSignReady || signMutation.isPending}
              >
                {signMutation.isPending ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                {isSignReady ? "Confirmer et Signer" : "Remplissez les champs pour signer"}
              </Button>
            </CardContent>
          </Card>
        )}

        {isSigned && (
          <Card className="border-2 border-emerald-500/20 bg-emerald-50 shadow-md rounded-2xl mt-8">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="font-extrabold text-2xl text-emerald-800 mb-2">Devis signé et validé</h3>
              <p className="text-slate-600 text-lg">Merci de votre confiance. Ce document a bien été enregistré.</p>
              {/* On peut afficher le nom du signataire s'il est renvoyé par le backend dans quote.signature_name */}
              {quote.signature_name && <p className="text-sm text-slate-500 mt-4 border-t border-emerald-200/50 pt-4">Signé par : <strong>{quote.signature_name}</strong> le {formatDate(quote.updated_at)}</p>}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-slate-500 pt-8 pb-4">
          {company.name && <p className="font-bold text-slate-700 mb-1">{company.name}</p>}
          {company.address && <p>{company.address} {company.postal_code} {company.city}</p>}
          <div className="flex justify-center gap-4 mt-2">
             {company.phone && <span>Tél : {company.phone}</span>}
             {company.email && <span>Email : {company.email}</span>}
          </div>
          
          <div className="mt-10 flex justify-center opacity-80 hover:opacity-100 transition-opacity">
            <PoweredBy />
          </div>
        </div>
      </main>
      
      <Toaster richColors position="top-center" />
    </div>
  );
};

export default PortalQuotePage;